import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Sparkles, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AddTaskDialog } from '@/components/marketing/AddTaskDialog';
import {
  useTasks,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useSuggestTasks,
  type TaskRow,
  type TaskType,
  type SuggestedTask,
} from '@/hooks/data/useMarketingTasks';
import { formatDate } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/kalender')({
  component: KalenderPage,
});

const TYPE_FALLBACKS: Record<TaskType, string> = {
  post: 'Post',
  story: 'Story',
  call: 'Anruf',
  follow_up: 'Follow-up',
  newsletter: 'Newsletter',
  video: 'Video',
  blog: 'Blog',
  other: 'Sonstiges',
};

// Returns the Monday of the week that is `offsetWeeks` weeks away from today.
function mondayOfWeek(offsetWeeks: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dow + offsetWeeks * 7);
  return d;
}

function formatDateRange(start: Date, end: Date): string {
  return `${formatDate(start, { day: '2-digit', month: '2-digit', year: 'numeric' })} – ${formatDate(end, { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
}

function msToDateValue(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// ─── Task chip ────────────────────────────────────────────────────────────────

interface TaskChipProps {
  task: TaskRow;
  onToggle: (checked: boolean) => void;
  onDelete: () => void;
}

function TaskChip({ task, onToggle, onDelete }: TaskChipProps) {
  const { t } = useTranslation();
  const isDone = task.status === 'done';

  return (
    <div
      className={`group flex items-start gap-2 rounded-lg border px-2.5 py-2 text-sm transition-colors ${
        isDone ? 'border-line bg-paper-2 opacity-60' : 'border-line bg-card'
      }`}
    >
      <input
        type="checkbox"
        checked={isDone}
        onChange={(e) => onToggle(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[hsl(var(--brand-gold))]"
        aria-label={isDone ? t('kalender.mark_open', 'Als offen markieren') : t('kalender.mark_done', 'Als erledigt markieren')}
      />
      <div className="min-w-0 flex-1">
        <p className={`truncate font-medium leading-tight ${isDone ? 'line-through text-ink-3' : 'text-ink'}`}>
          {task.title}
        </p>
        <span className="mt-0.5 inline-block rounded-full bg-paper-2 px-1.5 py-px text-[10px] uppercase tracking-wide text-ink-3">
          {t(`kalender.type_${task.task_type}`, TYPE_FALLBACKS[task.task_type])}
        </span>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="shrink-0 rounded p-0.5 text-ink-3 opacity-0 transition group-hover:opacity-100 hover:text-[hsl(var(--danger))]"
        aria-label={t('common.delete', 'Löschen')}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Day column ───────────────────────────────────────────────────────────────

interface DayColumnProps {
  day: Date;
  tasks: TaskRow[];
  isToday: boolean;
  onToggle: (id: string, checked: boolean) => void;
  onDelete: (id: string) => void;
  onAdd: (dayMs: number) => void;
}

function DayColumn({ day, tasks, isToday, onToggle, onDelete, onAdd }: DayColumnProps) {
  const { t } = useTranslation();
  const dayMs = day.getTime();

  return (
    <div
      className={`flex flex-col gap-2 rounded-xl border p-3 ${
        isToday ? 'border-[hsl(var(--brand-gold))] ring-1 ring-[hsl(var(--brand-gold)/0.3)]' : 'border-line'
      }`}
    >
      {/* Day header */}
      <div className="flex items-center justify-between gap-1">
        <div>
          <p
            className={`text-xs font-semibold uppercase tracking-wide ${
              isToday ? 'text-[hsl(var(--brand-gold))]' : 'text-ink-3'
            }`}
          >
            {formatDate(day, { weekday: 'short' })}
          </p>
          <p className={`text-sm font-medium ${isToday ? 'text-[hsl(var(--brand-gold))]' : 'text-ink'}`}>
            {formatDate(day, { day: '2-digit', month: '2-digit' })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onAdd(dayMs)}
          className="rounded-md p-1 text-ink-3 hover:bg-paper-2 hover:text-ink"
          aria-label={t('kalender.add', 'Aufgabe')}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Tasks */}
      <div className="flex flex-col gap-1.5">
        {tasks.length === 0 ? (
          <p className="py-2 text-center text-xs text-ink-3">—</p>
        ) : (
          tasks.map((task) => (
            <TaskChip
              key={task.id}
              task={task}
              onToggle={(checked) => onToggle(task.id, checked)}
              onDelete={() => onDelete(task.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── AI suggestions panel ─────────────────────────────────────────────────────

interface SuggestPanelProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  suggestions: SuggestedTask[];
  weekStartMs: number;
  onAccept: (s: SuggestedTask) => Promise<void>;
  onAcceptAll: () => Promise<void>;
  onRemove: (idx: number) => void;
}

function SuggestPanel({
  open,
  onOpenChange,
  suggestions,
  weekStartMs,
  onAccept,
  onAcceptAll,
  onRemove,
}: SuggestPanelProps) {
  const { t } = useTranslation();
  const [accepting, setAccepting] = useState<number | null>(null);
  const [acceptingAll, setAcceptingAll] = useState(false);

  const dayLabel = (offset: number) => {
    const d = new Date(weekStartMs + offset * 86400000);
    return formatDate(d, { weekday: 'short', day: '2-digit', month: '2-digit' });
  };

  const handleAccept = async (s: SuggestedTask, idx: number) => {
    setAccepting(idx);
    try {
      await onAccept(s);
      onRemove(idx);
    } finally {
      setAccepting(null);
    }
  };

  const handleAcceptAll = async () => {
    setAcceptingAll(true);
    try {
      await onAcceptAll();
    } finally {
      setAcceptingAll(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('kalender.suggestions_title', 'AI-Vorschläge für diese Woche')}</DialogTitle>
        </DialogHeader>

        {suggestions.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-3">
            {t('kalender.suggestions_empty', 'Alle Vorschläge wurden übernommen.')}
          </p>
        ) : (
          <div className="space-y-3">
            {suggestions.map((s, idx) => (
              <div key={idx} className="flex items-start gap-3 rounded-lg border border-line p-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-ink">{s.title}</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-paper-2 px-1.5 py-px text-[10px] uppercase tracking-wide text-ink-3">
                      {t(`kalender.type_${s.task_type}`, TYPE_FALLBACKS[s.task_type])}
                    </span>
                    <span className="text-xs text-ink-3">{dayLabel(s.day_offset)}</span>
                  </div>
                  {s.notes && <p className="mt-1 text-xs text-ink-3">{s.notes}</p>}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAccept(s, idx)}
                  disabled={accepting === idx || acceptingAll}
                >
                  {accepting === idx ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    t('kalender.accept', 'Übernehmen')
                  )}
                </Button>
              </div>
            ))}

            {suggestions.length > 1 && (
              <Button
                className="w-full"
                onClick={handleAcceptAll}
                disabled={acceptingAll || accepting !== null}
              >
                {acceptingAll ? (
                  <>
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    {t('common.loading', 'Lädt …')}
                  </>
                ) : (
                  t('kalender.accept_all', 'Alle übernehmen')
                )}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function KalenderPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();

  const [weekOffset, setWeekOffset] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [addDefaultDate, setAddDefaultDate] = useState<number | undefined>(undefined);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedTask[]>([]);

  const weekStart = mondayOfWeek(weekOffset);
  const weekStartMs = weekStart.getTime();
  const weekEndMs = weekStartMs + 7 * 86400000;

  // 7 day objects for the grid columns
  const days = Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(weekStart);
    dd.setDate(weekStart.getDate() + i);
    return dd;
  });

  // Today midnight for "is today" check and overdue filter
  const todayStart = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  const todayStartMs = todayStart.getTime();

  // Tasks for this week
  const { data: tasks = [] } = useTasks({ from: weekStartMs, to: weekEndMs });

  // All open tasks (for overdue strip) — fetch without upper bound, filter client-side
  const { data: openTasks = [] } = useTasks({ from: 0 });
  // due_date arrives as an ISO string at runtime (Drizzle timestamp_ms → Date
  // → JSON), though the type says number — coerce defensively before comparing.
  const overdueTasks = openTasks.filter(
    (task) => task.status === 'open' && new Date(task.due_date).getTime() < todayStartMs,
  );

  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const createTask = useCreateTask();
  const suggest = useSuggestTasks();

  // Group week tasks by day
  const tasksByDay = days.map((day) => {
    const dayMs = day.getTime();
    return tasks.filter((task) => {
      const due = new Date(task.due_date).getTime();
      return due >= dayMs && due < dayMs + 86400000;
    });
  });

  const handleToggle = (id: string, checked: boolean) => {
    updateTask.mutate({ id, status: checked ? 'done' : 'open' });
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: t('kalender.delete_title', 'Aufgabe löschen?'),
      body: t('kalender.delete_body', 'Diese Aufgabe wird endgültig entfernt.'),
      destructive: true,
    });
    if (!ok) return;
    deleteTask.mutate(id);
  };

  const handleAddForDay = (dayMs: number) => {
    setAddDefaultDate(dayMs + 12 * 3600000); // noon of that day
    setAddOpen(true);
  };

  const handleSuggest = async () => {
    try {
      const result = await suggest.mutateAsync();
      setSuggestions(result);
      setSuggestOpen(true);
    } catch {
      toast.error(t('kalender.suggest_error', 'Vorschläge konnten nicht geladen werden.'));
    }
  };

  const acceptSuggestion = async (s: SuggestedTask) => {
    await createTask.mutateAsync({
      title: s.title,
      taskType: s.task_type,
      dueDate: weekStartMs + s.day_offset * 86400000 + 12 * 3600000,
      notes: s.notes,
      source: 'ai',
    });
  };

  const acceptAllSuggestions = async () => {
    await Promise.all(suggestions.map((s) => acceptSuggestion(s)));
    setSuggestions([]);
  };

  const removeSuggestion = (idx: number) => {
    setSuggestions((prev) => prev.filter((_, i) => i !== idx));
  };

  // Week range label: Mon – Sun
  const weekEnd = new Date(weekEndMs - 1);
  const weekRangeLabel = formatDateRange(weekStart, weekEnd);

  return (
    <section className="space-y-6">
      {/* Page header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-ink">
            {t('kalender.title', 'Marketing-Kalender')}
          </h2>
          <p className="text-sm text-ink-3">
            {t('kalender.subtitle', 'Wöchentliche Marketing-Aufgaben planen und verfolgen.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSuggest}
            disabled={suggest.isPending}
          >
            {suggest.isPending ? (
              <Loader2 className="me-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="me-2 h-4 w-4" />
            )}
            {t('kalender.ai_suggest', 'AI-Vorschläge')}
          </Button>
          <Button
            onClick={() => {
              setAddDefaultDate(undefined);
              setAddOpen(true);
            }}
          >
            <Plus className="me-2 h-4 w-4" />
            {t('kalender.add', 'Aufgabe')}
          </Button>
        </div>
      </header>

      {/* Week navigation */}
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setWeekOffset((o) => o - 1)}
          aria-label={t('kalender.prev_week', 'Vorherige Woche')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[220px] text-center text-sm font-medium text-ink">
          {weekRangeLabel}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setWeekOffset((o) => o + 1)}
          aria-label={t('kalender.next_week', 'Nächste Woche')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        {weekOffset !== 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWeekOffset(0)}
          >
            {t('kalender.this_week', 'Diese Woche')}
          </Button>
        )}
      </div>

      {/* Overdue strip */}
      {overdueTasks.length > 0 && (
        <div className="rounded-xl border border-[hsl(var(--warning))] bg-[hsl(var(--warning)/0.05)] p-4">
          <p className="mb-3 text-sm font-semibold text-[hsl(var(--warning))]">
            {t('kalender.overdue', 'Überfällig')} ({overdueTasks.length})
          </p>
          <div className="flex flex-col gap-1.5">
            {overdueTasks.map((task) => (
              <TaskChip
                key={task.id}
                task={task}
                onToggle={(checked) => handleToggle(task.id, checked)}
                onDelete={() => handleDelete(task.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* 7-day grid */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-7">
        {days.map((day, i) => {
          const dayMs = day.getTime();
          const isToday = dayMs === todayStartMs;
          return (
            <DayColumn
              key={msToDateValue(dayMs)}
              day={day}
              tasks={tasksByDay[i] ?? []}
              isToday={isToday}
              onToggle={handleToggle}
              onDelete={(id) => handleDelete(id)}
              onAdd={handleAddForDay}
            />
          );
        })}
      </div>

      {/* Dialogs */}
      <AddTaskDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        defaultDate={addDefaultDate}
      />

      <SuggestPanel
        open={suggestOpen}
        onOpenChange={setSuggestOpen}
        suggestions={suggestions}
        weekStartMs={weekStartMs}
        onAccept={acceptSuggestion}
        onAcceptAll={acceptAllSuggestions}
        onRemove={removeSuggestion}
      />
    </section>
  );
}
